import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getContact, updateContact, deleteContact, starContact, generateDrafts } from '../api';
import { safeErr, isValidPhone } from '../App';

const SECTORS=['IT & Software','Healthcare','Manufacturing','Retail','Education','Logistics','Finance','Real Estate','Food & Agri'];

export default function ContactDetail({ showToast }) {
  const { id } = useParams();
  const nav    = useNavigate();
  const [contact,  setContact]  = useState(null);
  const [editing,  setEditing]  = useState(false);
  const [form,     setForm]     = useState({});
  const [saving,   setSaving]   = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [expandedEmail, setExpandedEmail] = useState(null);

  const load = () => getContact(id).then(r => { setContact(r.data); setForm(r.data); });
  useEffect(() => { load(); }, [id]);

  const handleDraft = async () => {
    setDrafting(true);
    try {
      const r = await generateDrafts([Number(id)]);
      if (r.data.created > 0) {
        showToast('✦ Draft created — review it in AI Drafts');
        nav('/drafts');
      } else {
        showToast('Could not generate a draft for this contact', 'error');
      }
    } catch(e) { showToast('Draft generation failed', 'error'); }
    finally { setDrafting(false); }
  };

  const save = async () => {
    if (!isValidPhone(form.phone)) { showToast('Enter a valid phone number', 'error'); return; }
    setSaving(true);
    const { id:_, created_at, updated_at, emails, source, lead_id, ...patch } = form;
    try {
      await updateContact(id, patch);
      setEditing(false); showToast('Contact updated'); load();
    } catch(e) { showToast(safeErr(e,'Error saving contact'), 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this contact permanently?')) return;
    await deleteContact(id); showToast('Contact deleted'); nav('/contacts');
  };

  const handleStar = async () => { await starContact(id); load(); };

  if (!contact) return <div className="loading-center"><div className="spinner spinner-lg"/></div>;

  const initials = contact.name.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
  const set = (k,v) => setForm(p=>({...p,[k]:v}));
  const emails = contact.emails || [];

  return (
    <div>
      {/* Header */}
      <div className="card">
        <div className="card-body">
          <div style={{display:'flex',alignItems:'flex-start',gap:14}}>
            <div className="avatar av-lg av-blue" style={{flexShrink:0}}>{initials}</div>
            <div style={{flex:1}}>
              {editing ? (
                <div className="form-row" style={{marginBottom:8}}>
                  <input className="form-input" value={form.name||''} onChange={e=>set('name',e.target.value)} placeholder="Full name"/>
                  <input className="form-input" value={form.email||''} onChange={e=>set('email',e.target.value)} placeholder="Email"/>
                </div>
              ) : (
                <>
                  <div style={{fontSize:20,fontWeight:700}}>{contact.name}</div>
                  <div style={{fontSize:13,color:'var(--text-2)',marginTop:3}}>
                    {[contact.role,contact.company,contact.sector].filter(Boolean).join(' · ')}
                  </div>
                </>
              )}
              <div style={{display:'flex',gap:6,marginTop:10,flexWrap:'wrap'}}>
                {contact.source==='lead' && <span className="pill pill-blue">From lead</span>}
                {contact.starred && <span className="pill pill-amber">★ Starred</span>}
                {contact.tags && contact.tags.split(',').filter(Boolean).map(t=>{
                  const isClient = t.trim().toLowerCase()==='client';
                  return (
                    <span key={t} style={isClient
                      ? {background:'var(--green-bg)',border:'1px solid var(--green-border)',borderRadius:20,padding:'2px 9px',fontSize:11,fontWeight:600,color:'var(--green)'}
                      : {background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:20,padding:'2px 9px',fontSize:11,color:'var(--text-2)'}}>
                      {isClient ? '✓ Client' : t.trim()}
                    </span>
                  );
                })}
              </div>
            </div>
            <div style={{display:'flex',gap:8,flexShrink:0,flexWrap:'wrap'}}>
              <button style={{background:'none',border:'1px solid var(--border)',borderRadius:'var(--r-sm)',padding:'6px 10px',cursor:'pointer',fontSize:17,color:contact.starred?'#D97706':'var(--text-2)'}}
                onClick={handleStar}>{contact.starred?'★':'☆'}</button>
              {editing ? (
                <>
                  <button className="btn btn-primary btn-sm" disabled={saving} onClick={save}>{saving?'Saving…':'Save'}</button>
                  <button className="btn btn-sm" onClick={()=>{setEditing(false);setForm(contact);}}>Cancel</button>
                </>
              ) : (
                <>
                  <button className="btn btn-sm" onClick={()=>setEditing(true)}>✎ Edit</button>
                  <button className="btn btn-sm" disabled={drafting} onClick={handleDraft}>
                    {drafting ? <><span className="spinner spinner-sm"/>Researching…</> : '✦ Draft AI email'}
                  </button>
                  <button className="btn btn-primary btn-sm"
                    onClick={()=>nav(`/email-manager?compose=1&to=${contact.email}&name=${encodeURIComponent(contact.name)}&cid=${contact.id}`)}>
                    ✉ Email
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={handleDelete}>✕ Delete</button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
        {/* Left: details + notes */}
        <div>
          <div className="card">
            <div className="card-header"><span className="card-title">Contact details</span></div>
            <div className="card-body">
              {editing ? (
                <>
                  <div className="form-row">
                    <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={form.phone||''} onChange={e=>set('phone',e.target.value)}/></div>
                    <div className="form-group"><label className="form-label">Location</label><input className="form-input" value={form.location||''} onChange={e=>set('location',e.target.value)}/></div>
                  </div>
                  <div className="form-row">
                    <div className="form-group"><label className="form-label">Role</label><input className="form-input" value={form.role||''} onChange={e=>set('role',e.target.value)}/></div>
                    <div className="form-group"><label className="form-label">Company</label><input className="form-input" value={form.company||''} onChange={e=>set('company',e.target.value)}/></div>
                  </div>
                  <div className="form-row">
                    <div className="form-group"><label className="form-label">Sector</label>
                      <select className="form-select" value={form.sector||''} onChange={e=>set('sector',e.target.value)}>
                        <option value="">Select</option>{SECTORS.map(s=><option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="form-group"><label className="form-label">Tags</label><input className="form-input" value={form.tags||''} onChange={e=>set('tags',e.target.value)}/></div>
                  </div>
                  <div className="form-row">
                    <div className="form-group"><label className="form-label">Website</label><input className="form-input" value={form.website||''} onChange={e=>set('website',e.target.value)}/></div>
                    <div className="form-group"><label className="form-label">LinkedIn</label><input className="form-input" value={form.linkedin||''} onChange={e=>set('linkedin',e.target.value)}/></div>
                  </div>
                </>
              ) : (
                <>
                  {contact.next_followup && (
                    <div style={{marginBottom:12,padding:'8px 10px',background:'var(--amber-bg)',border:'1px solid var(--amber-border)',borderRadius:'var(--r)'}}>
                      <div style={{fontSize:11,fontWeight:600,color:'var(--amber)',marginBottom:2}}>↻ Follow-up scheduled</div>
                      <div style={{fontSize:12,color:'var(--text)'}}>
                        #{(contact.followup_count||0)+1} · {new Date(contact.next_followup).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}
                      </div>
                    </div>
                  )}
                  {!contact.next_followup && contact.followup_count>0 && (
                    <div style={{marginBottom:12,padding:'8px 10px',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:'var(--r)'}}>
                      <div style={{fontSize:11,color:'var(--text-2)'}}>
                        ✓ {contact.followup_count} follow-up{contact.followup_count!==1?'s':''} sent · sequence complete
                      </div>
                    </div>
                  )}
                  {contact.interest==='interested' && (
                    <div style={{marginBottom:12,padding:'6px 10px',background:'var(--green-bg)',border:'1px solid var(--green-border)',borderRadius:'var(--r)'}}>
                      <span style={{fontSize:12,color:'var(--green)',fontWeight:600}}>✓ Interested</span>
                    </div>
                  )}
                  {contact.interest==='not_interested' && (
                    <div style={{marginBottom:12,padding:'6px 10px',background:'var(--red-bg)',border:'1px solid var(--red-border)',borderRadius:'var(--r)'}}>
                      <span style={{fontSize:12,color:'var(--red)',fontWeight:600}}>✗ Not interested</span>
                    </div>
                  )}
                  {[['✉ Email',contact.email,true],['📞 Phone',contact.phone,false],['📍 Location',contact.location,false],
                   ['🏢 Company',contact.company,false],['👤 Role',contact.role,false],
                   ['🔗 Website',contact.website,false],['💼 LinkedIn',contact.linkedin,false],
                   ['📅 Added',new Date(contact.created_at).toLocaleDateString('en-IN'),false]
                  ].filter(([,v])=>v).map(([l,v,primary])=>(
                    <div key={l} style={{display:'flex',gap:10,marginBottom:10}}>
                      <span style={{fontSize:12,color:'var(--text-2)',minWidth:80,flexShrink:0}}>{l}</span>
                      <span style={{fontSize:13,color:primary?'var(--blue)':'var(--text)',wordBreak:'break-all'}}>{v}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
          <div className="card" style={{marginBottom:0}}>
            <div className="card-header"><span className="card-title">Notes</span></div>
            <div className="card-body">
              {editing ? (
                <textarea className="form-textarea" value={form.notes||''} onChange={e=>set('notes',e.target.value)} style={{minHeight:90}}/>
              ) : (
                <div style={{fontSize:13,color:contact.notes?'var(--text)':'var(--text-3)',lineHeight:1.7}}>
                  {contact.notes || 'No notes yet.'}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: email history */}
        <div className="card" style={{marginBottom:0}}>
          <div className="card-header">
            <span className="card-title">Email history ({emails.length})</span>
            <button className="btn btn-primary btn-sm"
              onClick={()=>nav(`/email-manager?compose=1&to=${contact.email}&name=${encodeURIComponent(contact.name)}&cid=${contact.id}`)}>
              + Compose
            </button>
          </div>
          <div style={{overflow:'auto',maxHeight:500}}>
            {emails.length===0 ? (
              <div className="empty-state" style={{padding:32}}>
                <div className="empty-icon">✉</div>
                <div className="empty-desc">No emails with this contact yet</div>
                <button className="btn btn-primary btn-sm"
                  onClick={()=>nav(`/email-manager?compose=1&to=${contact.email}&name=${encodeURIComponent(contact.name)}`)}>
                  Send first email
                </button>
              </div>
            ) : emails.map(em=>{
              const isOpen = expandedEmail === em.id;
              const bodyText = (em.body||'').replace(/<[^>]+>/g,' ').trim();
              return (
              <div key={em.id} style={{borderBottom:'1px solid var(--border)'}}>
                {/* Header row — click to expand */}
                <div
                  style={{padding:'12px 16px',cursor:'pointer',transition:'background .1s',
                    background: isOpen ? 'var(--blue-bg)' : ''}}
                  onClick={()=>setExpandedEmail(isOpen ? null : em.id)}
                  onMouseEnter={e=>{ if(!isOpen) e.currentTarget.style.background='var(--surface2)'; }}
                  onMouseLeave={e=>{ if(!isOpen) e.currentTarget.style.background=''; }}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                    <div style={{fontSize:13,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1}}>
                      {em.subject}
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:0,marginLeft:8}}>
                      <div style={{fontSize:11,color:'var(--text-2)'}}>
                        {new Date(em.sent_at).toLocaleDateString('en-IN')}
                      </div>
                      <span style={{fontSize:11,color:'var(--text-3)'}}>{isOpen?'▲':'▼'}</span>
                    </div>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <span className={`pill ${em.folder==='inbox'?'pill-green':'pill-blue'}`} style={{fontSize:10}}>
                      {em.folder==='inbox'?'Received':'Sent'}
                    </span>
                    {!isOpen && (
                      <span style={{fontSize:12,color:'var(--text-2)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                        {bodyText.slice(0,90)}
                      </span>
                    )}
                  </div>
                </div>
                {/* Expanded body */}
                {isOpen && (
                  <div style={{padding:'0 16px 14px',background:'var(--blue-bg)'}}>
                    <div style={{padding:'10px 12px',background:'var(--surface)',borderRadius:'var(--r)',
                      fontSize:13,lineHeight:1.7,color:'var(--text)',whiteSpace:'pre-wrap',
                      maxHeight:320,overflowY:'auto',border:'1px solid var(--border)'}}>
                      {bodyText || '(no body)'}
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
